import type { PermissionKey } from '@construction/shared';

/**
 * 权限 → i18n key（CR-038）。
 *
 * 显式映射而不是拼字符串：按 key 拼出来的翻译少一条不会报错，
 * 只会在界面上显示成 `roles.perm.site.create`，而 `locales.test.ts` 扫的是字面量，
 * 拼出来的 key 它也看不见（CR-027 那条教训）。这份 Record<PermissionKey, string>
 * 的类型检查保证每加一个权限这里都得补一行；`permission-labels.test.ts` 再查 key 真的在语言文件里。
 *
 * 单独一个文件、不引 store：测试要能在 node 里直接 import 它，
 * 而 store 一路引到 i18n 初始化，那里要 localStorage。
 *
 * 角色**名字**不在这里 —— 它是客户输入的数据，不翻译（和日报正文一样）。
 */
export const PERMISSION_LABEL_KEY: Record<PermissionKey, string> = {
  'site.create': 'roles.perm.siteCreate',
  'site.manage': 'roles.perm.siteManage',
  'site.viewAll': 'roles.perm.siteViewAll',
  'drawing.view': 'roles.perm.drawingView',
  'drawing.upload': 'roles.perm.drawingUpload',
  'drawing.export': 'roles.perm.drawingExport',
  'schedule.view': 'roles.perm.scheduleView',
  'schedule.upload': 'roles.perm.scheduleUpload',
  'schedule.export': 'roles.perm.scheduleExport',
  'dailyLog.write': 'roles.perm.dailyLogWrite',
  'dailyLog.view': 'roles.perm.dailyLogView',
  'dailyLog.export': 'roles.perm.dailyLogExport',
  'hiddenWork.write': 'roles.perm.hiddenWorkWrite',
  'hiddenWork.view': 'roles.perm.hiddenWorkView',
  'hiddenWork.export': 'roles.perm.hiddenWorkExport',
  'purchase.write': 'roles.perm.purchaseWrite',
  'purchase.view': 'roles.perm.purchaseView',
  'purchase.review': 'roles.perm.purchaseReview',
  'purchase.export': 'roles.perm.purchaseExport',
  'attendance.punch': 'roles.perm.attendancePunch',
  'attendance.view': 'roles.perm.attendanceView',
  'attendance.manage': 'roles.perm.attendanceManage',
  'attendance.export': 'roles.perm.attendanceExport',
  'user.manage': 'roles.perm.userManage',
  'role.manage': 'roles.perm.roleManage',
  'audit.view': 'roles.perm.auditView',
};

/** 勾选框下面那句「这个勾意味着什么」 */
export const PERMISSION_HINT_KEY: Record<PermissionKey, string> = {
  'site.create': 'roles.permHint.siteCreate',
  'site.manage': 'roles.permHint.siteManage',
  'site.viewAll': 'roles.permHint.siteViewAll',
  'drawing.view': 'roles.permHint.drawingView',
  'drawing.upload': 'roles.permHint.drawingUpload',
  'drawing.export': 'roles.permHint.drawingExport',
  'schedule.view': 'roles.permHint.scheduleView',
  'schedule.upload': 'roles.permHint.scheduleUpload',
  'schedule.export': 'roles.permHint.scheduleExport',
  'dailyLog.write': 'roles.permHint.dailyLogWrite',
  'dailyLog.view': 'roles.permHint.dailyLogView',
  'dailyLog.export': 'roles.permHint.dailyLogExport',
  'hiddenWork.write': 'roles.permHint.hiddenWorkWrite',
  'hiddenWork.view': 'roles.permHint.hiddenWorkView',
  'hiddenWork.export': 'roles.permHint.hiddenWorkExport',
  'purchase.write': 'roles.permHint.purchaseWrite',
  'purchase.view': 'roles.permHint.purchaseView',
  'purchase.review': 'roles.permHint.purchaseReview',
  'purchase.export': 'roles.permHint.purchaseExport',
  'attendance.punch': 'roles.permHint.attendancePunch',
  'attendance.view': 'roles.permHint.attendanceView',
  'attendance.manage': 'roles.permHint.attendanceManage',
  'attendance.export': 'roles.permHint.attendanceExport',
  'user.manage': 'roles.permHint.userManage',
  'role.manage': 'roles.permHint.roleManage',
  'audit.view': 'roles.permHint.auditView',
};

/** 分组标题。key 来自 shared 的 PERMISSION_GROUPS，测试查两边对得上 */
export const PERMISSION_GROUP_LABEL_KEY: Record<string, string> = {
  site: 'roles.group.site',
  drawing: 'roles.group.drawing',
  schedule: 'roles.group.schedule',
  dailyLog: 'roles.group.dailyLog',
  hiddenWork: 'roles.group.hiddenWork',
  purchase: 'roles.group.purchase',
  attendance: 'roles.group.attendance',
  user: 'roles.group.user',
  system: 'roles.group.system',
};
